import React, { useEffect, useState, useCallback } from "react";
import { ChevronLeft, ChevronRight, ArrowRight } from "lucide-react";
import { slides, heroChips, wa, stats } from "../mock";
import { ContactLink } from "./ContactLink";

export default function Hero() {
  const [active, setActive] = useState(0);
  const go = useCallback((i) => setActive((i + slides.length) % slides.length), []);

  useEffect(() => {
    const t = setInterval(() => setActive((a) => (a + 1) % slides.length), 4800);
    return () => clearInterval(t);
  }, []);

  return (
    <section id="home" className="relative grid-backdrop overflow-hidden"
      style={{ background: "linear-gradient(135deg, rgba(255,248,236,0.98), rgba(255,253,247,0.9)), radial-gradient(circle at 84% 18%, rgba(245,180,0,0.22), transparent 28%), linear-gradient(180deg, var(--cream) 0%, #fff 100%)" }}>
      <div data-testid="hero-content" className="home-hero-content container relative z-10">
        <div className="grid items-center gap-10 lg:grid-cols-[1fr_380px]">
          {/* Copy */}
          <div className="max-w-[760px]">
            <p className="eyebrow reveal">Corporate IT &amp; office asset marketplace</p>
            <h1 data-testid="hero-heading" className="hero-heading reveal mt-3 font-heading font-extrabold"
                style={{ color: "var(--green-950)" }}>
              Buy, Sell &amp; Source{" "}
              <span data-testid="hero-heading-accent" style={{ color: "var(--orange-700)" }}>IT &amp; Office</span>{" "}
              Assets
            </h1>
            <p className="reveal mt-4 max-w-[700px] font-heading text-[15px] md:text-[17px] font-bold leading-relaxed" style={{ color: "var(--green-900)" }}>
              Laptops | Desktops | Servers | Networking | UPS | Printers | CCTV | Office Furniture | AC | TV
            </p>
            <p className="reveal mt-4 max-w-[640px] text-[16px] font-medium" style={{ color: "#31463a" }}>
              Best price, quick pickup and professional coordination for bulk IT and office assets across Delhi NCR &amp; Bangalore.
            </p>

            <div className="reveal mt-8 flex flex-wrap gap-3">
              <a href="#estimate" data-testid="hero-sell-btn"
                 className="kp-button inline-flex items-center gap-2 rounded-xl px-6 py-3.5 font-heading font-bold text-white transition-transform hover:-translate-y-0.5"
                 style={{ background: "linear-gradient(135deg, var(--green-800), var(--green-950))", boxShadow: "0 16px 32px rgba(45,106,79,0.24)" }}>
                Sell Assets <ArrowRight size={18} />
              </a>
              <a href="/store" data-testid="hero-buy-btn"
                 className="kp-button inline-flex items-center gap-2 rounded-xl px-6 py-3.5 font-heading font-bold transition-transform hover:-translate-y-0.5"
                 style={{ background: "var(--yellow-500)", color: "var(--green-950)", boxShadow: "0 16px 28px rgba(245,180,0,0.24)" }}>
                Buy Equipment <ArrowRight size={18} />
              </a>
              <a href={wa()} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-btn"
                 className="kp-button inline-flex items-center gap-2.5 rounded-xl px-6 py-3.5 font-heading font-bold text-white transition-transform hover:-translate-y-0.5"
                 style={{ background: "linear-gradient(135deg, #25D366, #128C7E)", boxShadow: "0 16px 32px rgba(37,211,102,0.28)" }}>
                <img src="/assets/icons/whatsapp-clean-square.png" alt="" className="h-6 w-6" /> WhatsApp Quote
              </a>
            </div>

            <div className="reveal mt-7 flex flex-wrap gap-2.5">
              {heroChips.map((c) => (
                <span key={c} className="rounded-full border bg-white/75 px-3.5 py-2 text-[13.5px] font-semibold"
                      style={{ borderColor: "rgba(45,106,79,0.16)", color: "var(--green-950)", boxShadow: "0 8px 18px rgba(45,106,79,0.06)" }}>
                  {c}
                </span>
              ))}
            </div>
          </div>

          {/* Quote card */}
          <aside className="reveal">
            <div className="relative overflow-hidden rounded-2xl border p-7"
                 style={{ background: "var(--cream)", borderColor: "rgba(234,219,197,0.95)", boxShadow: "var(--shadow)" }}>
              <span className="inline-flex rounded-full px-3 py-1.5 text-[12.5px] font-bold"
                    style={{ background: "var(--green-100)", color: "var(--green-900)" }}>Quick Enquiry</span>
              <h2 data-testid="hero-quote-heading" className="mt-4 font-heading text-[28px] font-extrabold" style={{ color: "var(--green-950)" }}>Get a fast quote</h2>
              <p className="mt-2 text-[15px]" style={{ color: "var(--muted)" }}>
                Send photos, quantity, condition and city. Our team will respond with the next step.
              </p>
              <ContactLink testId="hero-quote-phone" variant="hero" national className="mt-5 w-full" />
              <ContactLink kind="email" testId="hero-quote-email" variant="hero" className="mt-1 w-full" />
            </div>
          </aside>
        </div>

        {/* Slider */}
        <div className="reveal relative mt-11 overflow-hidden rounded-2xl border bg-white"
             style={{ borderColor: "var(--line)", boxShadow: "var(--shadow)" }}>
          <div className="relative" style={{ height: "min(500px, 44vw)", minHeight: 280 }}>
            {slides.map((s, i) => (
              <figure key={s.caption} className={`kp-slide m-0 ${i === active ? "is-active" : ""}`}>
                <img src={s.img} alt={s.caption} className="h-full w-full object-cover" />
                <span className="absolute inset-0"
                      style={{ background: "linear-gradient(90deg, rgba(255,248,236,0.55) 0%, rgba(255,248,236,0.12) 42%, rgba(246,111,24,0.12) 100%), linear-gradient(180deg, transparent 44%, rgba(31,74,56,0.6) 100%)" }} />
                <figcaption className="absolute bottom-5 left-5 z-10 max-w-[80%] rounded-lg px-3.5 py-2 font-heading text-[15px] md:text-[18px] font-bold text-white"
                            style={{ background: "rgba(31,74,56,0.5)", backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)", textShadow: "0 2px 6px rgba(0,0,0,0.45)" }}>
                  {s.caption}
                </figcaption>
              </figure>
            ))}
            {slides.length > 1 && (
              <>
                <button onClick={() => go(active - 1)} aria-label="Previous" data-testid="hero-slide-previous"
                        className="absolute left-4 top-1/2 z-20 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full transition-colors"
                        style={{ background: "rgba(255,253,247,0.92)", color: "var(--green-950)", boxShadow: "0 10px 24px rgba(45,106,79,0.14)" }}>
                  <ChevronLeft size={24} />
                </button>
                <button onClick={() => go(active + 1)} aria-label="Next" data-testid="hero-slide-next"
                        className="absolute right-4 top-1/2 z-20 grid h-11 w-11 -translate-y-1/2 place-items-center rounded-full transition-colors"
                        style={{ background: "rgba(255,253,247,0.92)", color: "var(--green-950)", boxShadow: "0 10px 24px rgba(45,106,79,0.14)" }}>
                  <ChevronRight size={24} />
                </button>
                <div className="absolute inset-x-0 bottom-4 z-20 flex justify-center gap-1">
                  {slides.map((s, i) => (
                    <button key={s.caption} onClick={() => go(i)} aria-label={`Show image ${i + 1}`} data-testid={`hero-slide-${i + 1}`}
                            className="grid h-6 place-items-center px-1.5">
                      <span className="block h-2.5 rounded-full transition-all"
                            style={{ width: i === active ? 28 : 10, background: i === active ? "var(--yellow-500)" : "rgba(255,255,255,0.75)" }} />
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Stats strip */}
        <div className="reveal mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className="rounded-2xl border bg-white/70 px-5 py-5 text-center"
                 style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.06)" }}>
              <div className="font-heading text-[30px] font-extrabold" style={{ color: "var(--green-900)" }}>{s.value}</div>
              <div className="mt-0.5 text-[13px] font-semibold" style={{ color: "var(--muted)" }}>{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
